/**
 * 판을 지켜보다 소리 낼 일을 알린다. 틱이 돈 뒤마다 `observe`를 부르면, 지난번에 본 상태와 견줘 새로 난 일을 돌려준다.
 * 싸우는 판(`GameLoop.onTick`)과 다시 보기(`ReplayLoop.onStep`)가 같은 것을 쓴다 — 다시 볼 때도 같은 일에 소리가 난다.
 *
 * **읽기만 한다.** 시뮬레이션의 상태를 바꾸지 않고 난수도 쓰지 않는다 — 소리를 켜든 끄든 판의 결과와 리플레이는 같다
 * (`tests/audio.test.ts`가 지켜본 판과 안 본 판의 체크섬을 틱마다 견준다).
 *
 * 보는 것은 판이 이미 내놓는 값뿐이다:
 * - 장수 기술: `roarCount`가 늘었다(그리기 전용 횟수) + `skillCode`.
 * - 동료 기술: `mateCount[분대]`가 늘었다.
 * - 대장: `headsAlive`(적장)·`chiefsAlive`(적장 + 부장)가 줄었다. 살아서 달아난 병사는 `alive`가 0이 되지만
 *   (`tryExit`), 대장은 달아나지 않으므로 이 수는 쓰러진 것만 센다.
 * - 장수: `leaderAlive`.
 */
import { SKILL_CODE } from '../sim/skills.ts';
import type { World } from '../sim/world.ts';
import type { CueId } from './cues.ts';

const SKILL_CUE: Record<number, CueId> = {
  [SKILL_CODE.roar]: 'skill.roar',
  [SKILL_CODE.phalanx]: 'skill.phalanx',
  [SKILL_CODE.volley]: 'skill.volley',
};

export class BattleSoundObserver {
  private roar = 0;
  private readonly mates: number[] = [];
  private heads = 0;
  private chiefs = 0;
  private leader = true;
  private readonly out: CueId[] = [];

  constructor(world: World) {
    this.reset(world);
  }

  /** 지금 상태를 기준으로 삼는다. 판을 열 때, 그리고 그리지 않고 건너뛴 뒤(되감기)에 부른다 — 건너뛴 사이의 일은 내지 않는다. */
  reset(w: World): void {
    this.roar = w.roarCount;
    this.mates.length = 0;
    for (let sq = 0; sq < w.mateCount.length; sq++) this.mates.push(w.mateCount[sq]);
    this.heads = w.headsAlive();
    this.chiefs = w.chiefsAlive();
    this.leader = w.leaderAlive();
  }

  /** 지난번 뒤로 새로 난 일. 돌려준 배열은 다음 부름에 다시 쓴다(쥐고 있지 않는다). */
  observe(w: World): readonly CueId[] {
    const out = this.out;
    out.length = 0;
    if (w.roarCount !== this.roar) {
      this.roar = w.roarCount;
      out.push(SKILL_CUE[w.skillCode] ?? 'skill.roar');
    }
    let mate = false;
    for (let sq = 0; sq < this.mates.length; sq++) {
      if (w.mateCount[sq] === this.mates[sq]) continue;
      this.mates[sq] = w.mateCount[sq];
      mate = true;
    }
    if (mate) out.push('mate.skill');
    const heads = w.headsAlive();
    const chiefs = w.chiefsAlive();
    // 적장이 쓰러졌으면 그 소리 하나만 — 같은 틱에 부장이 함께 쓰러져도 큰 쪽을 낸다.
    if (heads < this.heads) out.push('chief.fall');
    else if (chiefs < this.chiefs) out.push('sub.fall');
    this.heads = heads;
    this.chiefs = chiefs;
    const leader = w.leaderAlive();
    if (this.leader && !leader) out.push('hero.fall');
    this.leader = leader;
    return out;
  }
}
